import React, { useCallback } from 'react'
import type { Task } from '@ztechapps/common.contract'
import { TaskStatusEnum } from '@ztechapps/common.contract'
import { TaskBoardGroupByEnum, TaskViewMode } from '@ztechapps/task.common'
import { ShowIf } from '@ztechapps/webui.util'
import { TaskViewSwitcher } from '../TaskViewSwitcher'
import { TaskBoard } from '../TaskBoard'
import { TaskCalendar } from '../TaskCalendar'
import { useGetEvents } from '../TaskCalendar/useGetEvents'
import { useTaskTooltipPopOver } from '../TaskTooltipPopOver/useTaskTooltipPopOver'
import { EmbeddedTaskViewProvider, useEmbeddedTaskViewContext } from './Context'

const ALL_STATUSES = [
  TaskStatusEnum.Draft,
  TaskStatusEnum.InProgress,
  TaskStatusEnum.Complete,
  TaskStatusEnum.Cancelled,
]
const DEFAULT_HEIGHT = '70vh'

type EmbeddedTaskViewProps = {
  tasks: Task[]
  loading: boolean
  storageKey: string
  boardHeight?: string
  calendarHeight?: string
  canEdit?: boolean
  children: React.ReactNode
}

type EmbeddedTaskViewContentProps = {
  boardHeight?: string
  calendarHeight?: string
  canEdit?: boolean
  children: React.ReactNode
}

const EmbeddedTaskViewContent = ({
  boardHeight,
  calendarHeight,
  canEdit = true,
  children,
}: EmbeddedTaskViewContentProps) => {
  const { viewMode, setViewMode, tasks, loading } = useEmbeddedTaskViewContext()
  const { tooltipElement, openTooltip } = useTaskTooltipPopOver({ canEdit })
  const { eventsLocal } = useGetEvents(tasks)

  const handleBoardDoubleClick = useCallback(
    (taskId: string, event: React.MouseEvent) => {
      openTooltip(taskId, event.currentTarget as HTMLElement)
    },
    [openTooltip]
  )

  const handleCalendarDoubleClick = useCallback(
    (taskId: string, event: MouseEvent) => {
      openTooltip(taskId, event.target as HTMLElement)
    },
    [openTooltip]
  )

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noopDateChange = useCallback(() => {}, [])

  return (
    <div style={{ contain: 'inline-size' }}>
      <TaskViewSwitcher viewMode={viewMode} setViewMode={setViewMode} />
      <ShowIf condition={viewMode === TaskViewMode.List}>{children}</ShowIf>
      <ShowIf condition={viewMode === TaskViewMode.Board}>
        <TaskBoard
          tasks={tasks}
          loading={loading}
          visibleStatuses={ALL_STATUSES}
          groupBy={TaskBoardGroupByEnum.None}
          refetchQueries={[]}
          onDoubleClick={handleBoardDoubleClick}
          height={boardHeight ?? DEFAULT_HEIGHT}
          canEdit={canEdit}
        />
      </ShowIf>
      <ShowIf condition={viewMode === TaskViewMode.Calendar}>
        <TaskCalendar
          events={eventsLocal}
          onDateChange={noopDateChange}
          onEventDoubleClick={handleCalendarDoubleClick}
          height={calendarHeight ?? DEFAULT_HEIGHT}
          canEdit={canEdit}
        />
      </ShowIf>
      {tooltipElement}
    </div>
  )
}

export const EmbeddedTaskView = ({
  tasks,
  loading,
  storageKey,
  boardHeight,
  calendarHeight,
  canEdit = true,
  children,
}: EmbeddedTaskViewProps) => {
  return (
    <EmbeddedTaskViewProvider tasks={tasks} loading={loading} storageKey={storageKey}>
      <EmbeddedTaskViewContent boardHeight={boardHeight} calendarHeight={calendarHeight} canEdit={canEdit}>
        {children}
      </EmbeddedTaskViewContent>
    </EmbeddedTaskViewProvider>
  )
}
